import React from "react";
import { DetectionTrail } from "../../types/trail";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Clock, Award, Crosshair, FileText, User } from "lucide-react";
import DogHomePageIcon from "../icon/DogHomePageIcon";
import { formatDuration } from "../../utils/utils";
import { useAuth } from "../../contexts/AuthContext";

interface DetectionDetailsProps {
  trail: DetectionTrail;
}

export function DetectionDetails({ trail }: DetectionDetailsProps) {
  const { user } = useAuth();
  const isTrainer = user?.role.includes("trainer");

  return (
    <div className="space-y-6">
      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-2 border-purple-200 shadow-md">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="bg-purple-100 p-3 rounded-full">
                <Clock className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Durée</p>
                <p className="text-xl text-purple-900">{trail.duration ? formatDuration(trail.duration) : 'N/A'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        {trail.difficulty && (
            <Card className="border-2 border-yellow-200 shadow-md">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="bg-yellow-100 p-3 rounded-full">
                  <Award className="h-5 w-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Difficulté</p>
                  <p className="text-xl text-yellow-900">
                    {trail.difficulty === "Easy" ? "Facile" :
                     trail.difficulty === "Moderate" ? "Modérée" :
                     trail.difficulty === "Hard" ? "Difficile" : "Expert"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Details Grid */}
      <div className="grid grid-cols-1 gap-6">
        {/* Information Card */}
        <Card className="border-2 border-purple-200 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-purple-50 to-purple-100">
            <CardTitle className="text-purple-900">
              Informations de la détection
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 p-4 sm:p-6">
            <div className="flex items-start gap-3">
              <DogHomePageIcon className="h-5 w-5 text-purple-600 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">Chien</p>
                <p className="text-purple-900">{typeof trail.dog === "object" ? trail.dog.name : ""}</p>
              </div>
            </div>
            {trail.trainer && (
              <div className="flex items-start gap-3">
                <User className="h-5 w-5 text-purple-600 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground">Maître / Formateur</p>
                  <p className="text-purple-900">{trail.trainer}</p>
                </div>
              </div>
            )}
            {trail.targetScent && (
              <div className="flex items-start gap-3">
                <Crosshair className="h-5 w-5 text-purple-600 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground">Odeur cible</p>
                  <p className="text-purple-900">{trail.targetScent}</p>
                </div>
              </div>
            )}
            {trail.notes && (
              <div className="border-t pt-4 mt-4">
                <p className="text-sm text-muted-foreground mb-2 flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Notes
                </p>
                <p className="text-gray-700 whitespace-pre-wrap">{trail.notes}</p>
              </div>
            )}
            {isTrainer && trail.trainerComment && (
              <div className="border-t pt-4 mt-4">
                <p className="text-sm text-muted-foreground mb-2 flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Commentaire du formateur (privé)
                </p>
                <p className="text-gray-700 whitespace-pre-wrap">{trail.trainerComment}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
